/**
 * @suite/settings — the window plumbing behind the suite's shared settings
 * surface.
 *
 * EVERY application renders its settings through the shared two-column
 * `SettingsWindow` shell (`SettingsWindow.tsx`) inside a SEPARATE window,
 * never as an in-app modal:
 *
 *   - the window document is the application's own index.html booted with
 *     `?view=settings` (optionally `&tab=<key>`), which renders the bare
 *     settings page with no application chrome;
 *   - `openSettingsWindow` decides HOW that window is opened:
 *       1. this document is an embedded pane (iframe inside the hub) —
 *          hand the request to the hosting shell via postMessage, so the
 *          window survives into the Tauri context;
 *       2. this document runs under Tauri with `withGlobalTauri` — create
 *          (or focus) a `WebviewWindow` through the global API directly;
 *       3. plain web — open a browser popup;
 *   - `installSettingsWindowHost` is the receiving half: the hosting shell
 *     (the hub) installs it once and answers pane requests through its own
 *     opener. The Help Center window (`chrome.js`'s unclaimed Help Center
 *     fallback) rides the same plumbing: its pane posts
 *     `suite:open-help-center` with the same payload shape, plus an explicit
 *     `label` so the two windows can never collide.
 *
 * Framework-free on purpose: this file is imported by every application
 * (Tauri or browser, React or not) and must never pull a bundler-resolved
 * dependency.
 */

export const SETTINGS_VIEW_PARAM = "view";
export const SETTINGS_VIEW_VALUE = "settings";
export const SETTINGS_TAB_PARAM = "tab";
/** postMessage contract between an embedded pane and its hosting shell. */
export const SETTINGS_WINDOW_MESSAGE = "suite:open-settings";

/** postMessage contract for the Help Center window: an application pane asks
 *  its hosting shell to open the hub's /help-center page in its OWN window
 *  (chrome.js's unclaimed Help Center fallback). The payload shape is the
 *  settings request's, so one host answers both message types. */
export const HELP_CENTER_WINDOW_MESSAGE = "suite:open-help-center";

/** postMessage contract for the Help Center modal relay: an embedded pane's
 *  unclaimed Help Center fallback asks its hosting shell (the hub) to show
 *  the Help Center as the SHELL's own modal dialog — one real dialog box over
 *  every pane, never an iframe nested in the pane's document. The shell
 *  answers it through the `onHelpModal` callback of installSettingsWindowHost. */
export const HELP_CENTER_MODAL_MESSAGE = "suite:open-help-modal";

/**
 * Suite-wide storage key for the user's "open settings in a separate window"
 * preference (the toggle inside every application's settings panel). It is
 * deliberately a SUITE key, not per-app: how settings open is one behavior
 * across every application. Default is OFF — the in-app dialog (settings-window
 * plan).
 */
export const SETTINGS_WINDOW_PREFERENCE_KEY = "suite.settings.window";

/**
 * Suite-wide storage key for the user's "Open Help Center in a separate
 * window" preference (the toggle inside the settings panel). One behavior
 * across every application, like the settings preference above. Default is
 * OFF — the Help Center opens as the in-app modal dialog (help-window plan).
 */
export const HELP_CENTER_WINDOW_PREFERENCE_KEY = "suite.helpCenter.window";

export const SETTINGS_WINDOW_WIDTH = 980;
export const SETTINGS_WINDOW_HEIGHT = 680;
export const SETTINGS_WINDOW_MIN_WIDTH = 720;
export const SETTINGS_WINDOW_MIN_HEIGHT = 480;

/** The Help Center window's own size (chrome.js opens the same numbers). */
export const HELP_CENTER_WINDOW_WIDTH = 1040;
export const HELP_CENTER_WINDOW_HEIGHT = 720;

/** One settings-open request, whether posted by a pane or acted on by a host. */
export interface SettingsWindowRequest {
    /** The register app id — keys the window label (`settings-<app>`) */
    app: string;
    /** The absolute URL to load (`…/?view=settings[&tab=…]`) */
    url: string;
    /** Window title; defaults to "Settings" */
    title?: string;
    width?: number;
    height?: number;
    /** Explicit window label; defaults to `settings-<app>`. The Help Center
     *  window passes `helpcenter-<app>` so the two can never collide. */
    label?: string;
}

/** A host-side callback that actually opens (or focuses) the window. */
export type SettingsWindowOpener = (request: SettingsWindowRequest) => void;

interface TauriGlobal {
    webviewWindow?: {
        WebviewWindow?: {
            new (label: string, options: Record<string, unknown>): {
                once: (event: string, handler: () => void) => unknown;
            };
            getByLabel?: (
                label: string,
            ) => Promise<{ setFocus: () => Promise<void> } | null>;
        };
    };
    window?: {
        getCurrent?: () => { close: () => void };
    };
}

/** The `withGlobalTauri` bundle, when this document runs under Tauri. */
function tauriGlobal(): TauriGlobal | null {
    if (typeof window === "undefined") return null;
    const tauri = (window as unknown as Record<string, unknown>).__TAURI__;
    return tauri && typeof tauri === "object" ? (tauri as TauriGlobal) : null;
}

/** True when THIS document is the bare settings window (`?view=settings`). */
export function isSettingsWindowDocument(): boolean {
    if (typeof window === "undefined") return false;
    try {
        return (
            new URLSearchParams(window.location.search).get(SETTINGS_VIEW_PARAM) ===
            SETTINGS_VIEW_VALUE
        );
    } catch {
        return false;
    }
}

/** The `&tab=` deep link inside the settings window, when present. */
export function readSettingsWindowTab(): string | null {
    if (typeof window === "undefined") return null;
    try {
        return new URLSearchParams(window.location.search).get(SETTINGS_TAB_PARAM);
    } catch {
        return null;
    }
}

/**
 * The settings-window URL for THIS application's origin: the same index.html
 * booted straight into the bare settings surface (and, optionally, a
 * deep-linked section).
 */
export function settingsWindowUrl(tab?: string): string {
    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.set(SETTINGS_VIEW_PARAM, SETTINGS_VIEW_VALUE);
    if (tab) url.searchParams.set(SETTINGS_TAB_PARAM, tab);
    else url.searchParams.delete(SETTINGS_TAB_PARAM);
    return url.toString();
}

/**
 * The Help Center window's URL for THIS application's origin: the hub's
 * /help-center route booted as the whole content of its own window
 * (`?window=1`), the contract `HelpCenterEmbedPage`'s window mode closes.
 */
export function helpCenterWindowUrl(app?: string): string {
    const url = new URL(window.location.href);
    url.hash = "";
    url.pathname = "/help-center";
    url.search = "";
    url.searchParams.set("window", "1");
    if (app) url.searchParams.set("app", app);
    return url.toString();
}

/**
 * True when this document renders inside a hosting shell (the hub's pane
 * iframe) rather than at the top level of its own window.
 */
export function isEmbeddedPane(): boolean {
    try {
        return window.parent !== window;
    } catch {
        return true;
    }
}

/** The Tauri window label for one application's settings window. */
export function settingsWindowLabel(app: string): string {
    return `settings-${app}`;
}

/**
 * Create the settings window through the Tauri global API, focusing an
 * already-open one instead when possible. Returns false when this document
 * has no usable Tauri bundle (plain web), so the caller can fall back.
 */
async function tauriOpen(request: SettingsWindowRequest): Promise<boolean> {
    const WebviewWindow = tauriGlobal()?.webviewWindow?.WebviewWindow;
    if (!WebviewWindow) return false;
    const label = request.label ?? settingsWindowLabel(request.app);
    try {
        const existing = await WebviewWindow.getByLabel?.(label);
        if (existing) {
            await existing.setFocus();
            return true;
        }
    } catch {
        // Listing is unavailable (permission or API drift) — try to create.
    }
    const win = new WebviewWindow(label, {
        url: request.url,
        title: request.title ?? "Settings",
        width: request.width ?? SETTINGS_WINDOW_WIDTH,
        height: request.height ?? SETTINGS_WINDOW_HEIGHT,
        minWidth: SETTINGS_WINDOW_MIN_WIDTH,
        minHeight: SETTINGS_WINDOW_MIN_HEIGHT,
        center: true,
        resizable: true,
    });
    // Settle on either event: `created` means the window exists. `error` means
    // creation failed — re-check the label, because a concurrent opener may
    // have won the race (the window is on screen), but a permission gap or a
    // bad URL leaves NO window at all, and the caller must fall back instead
    // of reporting success over an empty screen.
    const created = await new Promise<boolean>((resolve) => {
        let settled = false;
        const done = (opened: boolean) => {
            if (!settled) {
                settled = true;
                resolve(opened);
            }
        };
        void win.once("tauri://created", () => done(true));
        void win.once("tauri://error", () => {
            void WebviewWindow.getByLabel?.(label)
                .then((existing) => done(Boolean(existing)))
                .catch(() => done(false));
        });
    });
    return created;
}

/** Browser-popup fallback for plain web documents. */
function webOpen(request: SettingsWindowRequest): void {
    const width = request.width ?? SETTINGS_WINDOW_WIDTH;
    const height = request.height ?? SETTINGS_WINDOW_HEIGHT;
    window.open(
        request.url,
        request.label ?? settingsWindowLabel(request.app),
        `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no,resizable=yes`,
    );
}

/**
 * Open this application's settings window. Call from the topbar's settings
 * handler (`chrome:settings`) and any other settings entry point.
 */
export function openSettingsWindow(
    request: SettingsWindowRequest,
): void {
    // 1. Embedded pane: the hosting shell owns the window (it can reach the
    //    Tauri API; this iframe cannot) — hand the request up.
    if (isEmbeddedPane()) {
        window.parent.postMessage(
            { type: SETTINGS_WINDOW_MESSAGE, ...request },
            "*",
        );
        return;
    }
    // 2. Tauri top-level window.
    void tauriOpen(request).then((opened) => {
        // 3. Plain web fallback.
        if (!opened) webOpen(request);
    });
}

/**
 * The receiving half, installed ONCE by a shell that hosts application
 * panes (the hub). Answers `suite:open-settings` and `suite:open-help-center`
 * postMessages from embedded panes through the given opener (default: Tauri,
 * else browser popup), and relays `suite:open-help-modal` — an embedded pane
 * asking the shell to show the Help Center as ITS OWN modal dialog — to the
 * given `onHelpModal` callback.
 */
export function installSettingsWindowHost(
    opener?: SettingsWindowOpener,
    onHelpModal?: () => void,
): () => void {
    const open: SettingsWindowOpener =
        opener ??
        ((request) => {
            void tauriOpen(request).then((opened) => {
                if (!opened) webOpen(request);
            });
        });
    const onMessage = (event: MessageEvent) => {
        const data = event.data as Record<string, unknown> | null;
        if (!data) return;
        if (data.type === HELP_CENTER_MODAL_MESSAGE) {
            onHelpModal?.();
            return;
        }
        if (
            data.type !== SETTINGS_WINDOW_MESSAGE &&
            data.type !== HELP_CENTER_WINDOW_MESSAGE
        )
            return;
        if (typeof data.url !== "string" || typeof data.app !== "string") return;
        open({
            app: data.app,
            url: data.url,
            title: typeof data.title === "string" ? data.title : undefined,
            width: typeof data.width === "number" ? data.width : undefined,
            height: typeof data.height === "number" ? data.height : undefined,
            label: typeof data.label === "string" ? data.label : undefined,
        });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
}

/**
 * Close the settings window from inside it (the shell's close button):
 * the Tauri window close when available, else the script-opened popup.
 */
export function closeSettingsWindow(): void {
    const getCurrent = tauriGlobal()?.window?.getCurrent;
    if (getCurrent) {
        try {
            getCurrent().close();
            return;
        } catch {
            // Permission or API drift — fall through to window.close().
        }
    }
    window.close();
}

/**
 * True when the user opted into opening settings as a separate window (the
 * toggle in the settings panel). Default is FALSE: settings open as the
 * in-app dialog, like they always have.
 */
export function isSettingsWindowPreferred(): boolean {
    try {
        return localStorage.getItem(SETTINGS_WINDOW_PREFERENCE_KEY) === "1";
    } catch {
        return false;
    }
}

/** Flip the separate-window preference (persisted suite-wide). */
export function setSettingsWindowPreferred(preferred: boolean): void {
    try {
        localStorage.setItem(SETTINGS_WINDOW_PREFERENCE_KEY, preferred ? "1" : "0");
    } catch {
        // Storage unavailable — the preference just does not persist.
    }
}

/**
 * True when the user opted into opening the Help Center as a separate window
 * (the toggle in the settings panel). Default is FALSE: the Help Center opens
 * as the in-app modal, like it always has.
 */
export function isHelpCenterWindowPreferred(): boolean {
    try {
        return localStorage.getItem(HELP_CENTER_WINDOW_PREFERENCE_KEY) === "1";
    } catch {
        return false;
    }
}

/** Flip the Help Center's separate-window preference (persisted suite-wide). */
export function setHelpCenterWindowPreferred(preferred: boolean): void {
    try {
        localStorage.setItem(HELP_CENTER_WINDOW_PREFERENCE_KEY, preferred ? "1" : "0");
    } catch {
        // Storage unavailable — the preference just does not persist.
    }
}

/**
 * THE Help Center entry point for every application: raises the in-app modal
 * by default, or the hub's /help-center window when the user opted in. Each
 * caller supplies both halves — `openInApp` is the application's existing
 * modal-open path, `request` describes its window (the `helpcenter-<app>`
 * label keeps it clear of the settings window).
 */
export function openAppHelpCenter(options: {
    request: SettingsWindowRequest;
    openInApp: () => void;
}): void {
    if (isHelpCenterWindowPreferred()) {
        openSettingsWindow(options.request);
    } else {
        options.openInApp();
    }
}

/**
 * THE settings entry point for every application: raises the in-app dialog
 * by default, or the shared settings window when the user opted in. Each
 * caller supplies both halves — `openInApp` is the application's existing
 * dialog-open path, `request` describes its window.
 */
export function openAppSettings(options: {
    request: SettingsWindowRequest;
    openInApp: () => void;
}): void {
    if (isSettingsWindowPreferred()) {
        openSettingsWindow(options.request);
    } else {
        options.openInApp();
    }
}
